import { useEffect, useState } from "react";
import type { ChangeEvent, Dispatch, ReactElement, ReactNode,
              SetStateAction } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { Abrechnungsquellen, ApiClient, Auftrag, Ansprechpartner, Benutzer,
              KonditionAuswahl, PositionsStand, Rechnung, Unternehmen } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen, useBeforeUnload } from "@kundeninfo/ui";
import { Belegvorschau } from "./Belegvorschau.js";
import { PositionsEditor, PlatzhalterTextfeld,
         laufzeitVorschauWerte } from "./PositionsEditor.js";
import type { PositionsEditorClient } from "./PositionsEditor.js";
import { Aufklapper, Kopfblock, useKopffaltung } from "./Kopffaltung.js";
import type { Kopffaltung } from "./Kopffaltung.js";
import { BelegParteien, PARTEIEN_KOPFFELDER } from "./BelegParteien.js";
import type { BelegParteienModalClient } from "./BelegParteien.js";
import { Zahlplan, ZAHLPLAN_KOPFFELDER, istZahlplanFeld } from "./Zahlplan.js";
import type { ZahlplanModalClient } from "./Zahlplan.js";
import { GeplanteFaelligkeiten } from "./GeplanteFaelligkeiten.js";
import type { FaelligkeitenClient } from "./GeplanteFaelligkeiten.js";
import { Rechnungsvorschlag } from "./Rechnungsvorschlag.js";
import type { VorschlagsClient } from "./Rechnungsvorschlag.js";
import { Abrechnung } from "./Abrechnung.js";
import type { AbrechnungsClient } from "./Abrechnung.js";
import { AuftragsVolumen } from "./AuftragsVolumen.js";
import type { VolumenClient } from "./AuftragsVolumen.js";
import { Anhaenge } from "./Anhaenge.js";
import type { AnhaengeClient } from "./Anhaenge.js";
import { AbrechnungsquellenKasten, hatQuellenStoerung, stoerungsText }
  from "./AbrechnungsquellenKasten.js";
import type { AbrechnungsquellenClient } from "./AbrechnungsquellenKasten.js";
import { formatBetrag, formatDatum } from "../format.js";
import { AUFTRAG_STATUS_LABEL } from "../vorgangsstatus.js";
import { apiMeldung, betragText, betragZahl, entwuerfeGleich,
         kopfBetragText, kopffeldAusFehler, kopffeldId, LAUFZEIT_KOPFFELDER,
         nettobetragLabel, oderNull,
         pdfMeldungsStil, speichereBlob, speichereParteien, speichereZahlplan,
         useParteienGriff, useZahlplanGriff } from "./vorgangsdetail.js";
import type { KopffeldTabelle, PdfMeldung } from "./vorgangsdetail.js";
// Die Klassennamen bleiben `kauf__` (Etappe 1, Aufgabe 2: keine Umbenennung an
// herausgelösten Bestandsteilen) — umgezogen ist in Fixrunde 1 nur die DATEI: Die Regeln
// dessen, was DIESER Baustein zeichnet, stehen jetzt hier statt im Stilblatt des Reiters.
import "./AuftragDetail.css";

/**
 * Das AUFTRAGSDETAIL als eigener Baustein (Bereichs-Spalten, Etappe 1, Aufgabe 2) — die
 * Schwester von AngebotDetail und aus demselben Grund herausgelöst: Der Akten-Reiter und
 * der Auftrags-BEREICH (Aufgabe 4) sollen buchstäblich DASSELBE Detail zeichnen.
 * Der Umbau ist rein strukturell.
 *
 * Was der Baustein trägt: Kopfzeile (Nummer, Kürzel, Titel, Status), Datenzeile (Betrag,
 * Laufzeit-Ende, Herkunft aus einem Angebot, Ansprechpartner), das Bearbeiten samt
 * Positions-Editor, die Statusaktionen, „Als PDF" mitsamt der `fields[]`-Befundliste und die
 * Auftrag→Rechnung-Naht.
 *
 * Was er NICHT weiss: dass es eine Kundenakte gibt. Liste, Anlegen (auch das aus einem
 * Angebot vorbelegte), Meldungsstreifen und die Frage, WELCHER Vorgang bearbeitet wird,
 * gehören dem Rahmen und kommen als Eigenschaften und Rückrufe herein.
 */

/** Nur die Klientenmethoden, die das DETAIL braucht — Liste und Anlegen bleiben beim
 *  Rahmen (siehe `AuftraegeClient` in views/KundeAuftraege.tsx). */
export type AuftragDetailClient = Pick<ApiClient,
  "updateSalesOrder" | "setSalesOrderStatus" | "listContacts" | "listUsers"
  | "listSelectablePaymentTerms" | "listPlaceholders" | "createInvoiceFromOrder"
  | "renderOrder"
  // Belegvorschau (24.09.2026): die Seitenbilder der Auftragsbestätigung.
  | "vorschauOrder">
  & PositionsEditorClient
  & BelegParteienModalClient
  & ZahlplanModalClient
  & FaelligkeitenClient
  & VorschlagsClient
  & AbrechnungsClient
  & VolumenClient
  // Der Kasten „Anhänge" (Paket „Textbausteine als Anhang", 24.09.2026) — am Auftrag NUR
  // lesend und mit dem Ausblenden-Schalter; kein Modal-Schlitz (siehe Kopfkommentar an der
  // Einhängung weiter unten).
  & AnhaengeClient
  // Der Kasten „Abrechnungsquellen" (Paket „Abrechnungsquellen", 29.09.2026).
  & AbrechnungsquellenClient;

/**
 * Die AUSWAHLLISTEN, die Anlege- und Bearbeitungsmaske gemeinsam brauchen. Sie werden vom
 * RAHMEN geladen (`useAuftraegeUmfeld`) und hier hereingereicht, nicht je Detail geholt:
 * Sonst zöge jede Listenzeile ihre eigenen Ansprechpartner, Benutzer, Konditionen und
 * Platzhalter — vier Abrufe mal zwanzig Zeilen, für Daten, die für alle dieselben sind.
 */
export type AuftraegeUmfeldDaten = {
  kontakte: Ansprechpartner[];
  benutzer: Benutzer[];
  konditionen: KonditionAuswahl[];
  platzhalter: string[];
};

/**
 * Lädt die Auswahllisten einer Auftrags-Ansicht: aktive Ansprechpartner des Kunden, aktive
 * Benutzer, die wählbaren Zahlungskonditionen und die Platzhalternamen.
 *
 * Scheitert einer der Abrufe, bleibt die Ansicht bedienbar — die Felder sind ohnehin
 * optional, und die Vorschau lässt dann schlicht alle Tokens stehen.
 */
export function useAuftraegeUmfeld(
  client: Pick<AuftragDetailClient,
    "listContacts" | "listUsers" | "listSelectablePaymentTerms" | "listPlaceholders">,
  kundeId: string,
): AuftraegeUmfeldDaten {
  const [kontakte, setKontakte] = useState<Ansprechpartner[]>([]);
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [konditionen, setKonditionen] = useState<KonditionAuswahl[]>([]);
  const [platzhalter, setPlatzhalter] = useState<string[]>([]);

  useEffect(() => {
    let ab = false;
    client.listContacts(kundeId)
      .then(({ items }) => { if (!ab) setKontakte(items.filter((k) => k.status === "ACTIVE")); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client, kundeId]);

  useEffect(() => {
    let ab = false;
    client.listUsers()
      .then(({ items }) => { if (!ab) setBenutzer(items.filter((b) => b.active)); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listSelectablePaymentTerms()
      .then(({ items }) => { if (!ab) setKonditionen(items); })
      .catch(() => { /* optionales Feld — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  useEffect(() => {
    let ab = false;
    client.listPlaceholders()
      .then(({ variablen }) => { if (!ab) setPlatzhalter(variablen); })
      .catch(() => { /* Auswahlhilfe — kein Aufhalten */ });
    return () => { ab = true; };
  }, [client]);

  return { kontakte, benutzer, konditionen, platzhalter };
}

type OrderStatus = Auftrag["status"];

/**
 * Die erlaubten Statusübergänge — bewusst hier NACHGEBILDET, nicht aus @kundeninfo/domain
 * importiert: web darf nur `ui` und `api-client` importieren (check-boundaries). Die Regel ist
 * die aus der Spec (salesOrderUebergangErlaubt): aus ACTIVE nach COMPLETED oder CANCELLED; beide
 * sind Endzustände und bieten nichts mehr an. Der Server prüft denselben Übergang noch einmal
 * (422), das hier ist die Bedienführung, nicht die Wahrheit.
 */
function erlaubteFolgestatus(von: OrderStatus): OrderStatus[] {
  if (von === "ACTIVE") return ["COMPLETED", "CANCELLED"];
  return [];
}

/** Klasse und Rolle der PDF-Rückmeldung im Block dieser Ansicht (siehe vorgangsdetail.ts) —
 *  eigener Blockname statt eines über die Ansichten geteilten. */
const PDF_MELDUNGS_STIL = pdfMeldungsStil("kauf");

/** Der heutige Kalendertag als ISO-Datum (YYYY-MM-DD) für „bestellt am". */
export function heuteAlsTag(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const tt = String(d.getDate()).padStart(2, "0");
  return `${String(d.getFullYear())}-${mm}-${tt}`;
}

/** Die Formularwerte. Alle Angaben sind Zeichenketten (ausser dem openEnded-Schalter), damit
 *  ein geleertes Feld erst beim Absenden zu null wird. */
export type AuftragEntwurf = {
  companyId: string;
  title: string;
  netAmount: string;
  contactId: string;
  responsibleUserId: string;
  paymentTermId: string;
  customerPurchaseOrderNumber: string;
  orderedOn: string;
  startDate: string;
  endDate: string;
  openEnded: boolean;
  /** Rohtexte MIT ihren `#{…}`-Tokens — gespeichert wird genau das, aufgelöst wird erst
   *  fürs PDF (die Vorschau darunter ist nur eine Lesehilfe). Am Auftrag sind sie das
   *  VEREINBARTE, nicht mehr das Angebotene. */
  introText: string;
  outroText: string;
  /**
   * Die Laufzeitfelder aus Migration 031. Die WERTE als Zeichenkette, damit ein geleertes
   * Feld beim Absenden zu `null` wird statt zu 0 — „keine Mindestlaufzeit" und „null
   * Monate" sind zwei verschiedene Aussagen.
   *
   * Die EINHEITEN behalten ihren Wert auch bei leerem Feld: Sonst würde eine einmal
   * gewählte Angabe „Jahre" durch ein Leeren des Zahlenfeldes still zu „Monate"
   * (dieselbe Überlegung wie bei `noticeReference` seit Migration 008). Vorher gab es hier
   * nur zwei nackte Monatszahlen (`minimumTermMonths`/`renewalPeriodMonths`) und keine
   * Vertragslaufzeit — Migration 031 macht daraus vier Paare aus Zahl und Einheit.
   */
  termValue: string;
  termUnit: "MONTHS" | "YEARS";
  minimumTermValue: string;
  minimumTermUnit: "MONTHS" | "YEARS";
  renewalPeriodValue: string;
  renewalPeriodUnit: "MONTHS" | "YEARS";
  noticePeriodValue: string;
  noticePeriodUnit: "MONTHS" | "YEARS";
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END";
  /** Die Periodenregel des Zahlplans (Migration 026). Beim Ableiten aus einem Angebot
   *  (Ruling Z10) kommt sie von DORT und ist im Anlegeformular gesperrt — siehe
   *  `auftragAnlegen.ts`; am gespeicherten Auftrag ist sie danach gewöhnlich änderbar. */
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

export function leererAuftragEntwurf(companies: Unternehmen[]): AuftragEntwurf {
  return {
    // Bei genau einem Unternehmen (der häufige Fall: eine Kundenbeziehung) ist es vorgewählt;
    // bei mehreren muss bewusst gewählt werden.
    companyId: companies.length === 1 ? companies[0]!.id : "",
    title: "", netAmount: "", contactId: "", responsibleUserId: "", paymentTermId: "",
    customerPurchaseOrderNumber: "", orderedOn: heuteAlsTag(), startDate: "", endDate: "",
    openEnded: false, introText: "", outroText: "",
    termValue: "", termUnit: "MONTHS",
    minimumTermValue: "", minimumTermUnit: "MONTHS",
    renewalPeriodValue: "", renewalPeriodUnit: "MONTHS",
    noticePeriodValue: "", noticePeriodUnit: "MONTHS", noticeReference: "TERM_END",
    periodBasis: "CALENDAR_YEAR",
  };
}

export function auftragEntwurfAus(a: Auftrag): AuftragEntwurf {
  return {
    companyId: a.companyId,
    title: a.title,
    netAmount: betragText(a.netAmount),
    contactId: a.contactId ?? "",
    responsibleUserId: a.responsibleUserId ?? "",
    paymentTermId: a.paymentTermId ?? "",
    customerPurchaseOrderNumber: a.customerPurchaseOrderNumber ?? "",
    orderedOn: a.orderedOn,
    startDate: a.startDate ?? "",
    endDate: a.endDate ?? "",
    openEnded: a.openEnded,
    introText: a.introText ?? "",
    outroText: a.outroText ?? "",
    termValue: a.termValue === null ? "" : String(a.termValue),
    termUnit: a.termUnit ?? "MONTHS",
    minimumTermValue: a.minimumTermValue === null ? "" : String(a.minimumTermValue),
    minimumTermUnit: a.minimumTermUnit ?? "MONTHS",
    renewalPeriodValue: a.renewalPeriodValue === null ? "" : String(a.renewalPeriodValue),
    renewalPeriodUnit: a.renewalPeriodUnit ?? "MONTHS",
    noticePeriodValue: a.noticePeriodValue === null ? "" : String(a.noticePeriodValue),
    // Beide Felder tragen im Vertrag ihre Aufzählung, der erzeugte Client also die enge
    // Vereinigung — hier bleibt nur der Vorgabewert für „noch keine Frist erfasst".
    noticePeriodUnit: a.noticePeriodUnit ?? "MONTHS",
    noticeReference: a.noticeReference ?? "TERM_END",
    periodBasis: a.periodBasis,
  };
}

/**
 * Die Kopffelder der Auftragsmaske: SERVER-Feldname → Endung der Feld-Id im Formular.
 * Begründung bei `KopffeldTabelle` (vorgangsdetail.ts) und `ANGEBOT_KOPFFELDER`.
 *
 * `endDate` steht darin, obwohl das Feld bei „Unbefristet" gar nicht gezeichnet wird: Genau
 * dann meldet der Server „Ein unbefristeter Auftrag hat kein Enddatum", und dann soll der
 * Block sich öffnen — sichtbar wird dabei der Schalter, um den es in Wahrheit geht.
 */
export const AUFTRAG_KOPFFELDER: KopffeldTabelle = {
  companyId: "company",
  title: "title",
  netAmount: "amount",
  orderedOn: "ordered",
  customerPurchaseOrderNumber: "po",
  paymentTermId: "payterm",
  contactId: "contact",
  responsibleUserId: "user",
  startDate: "start",
  endDate: "end",
  periodBasis: "periodbasis",
  ...LAUFZEIT_KOPFFELDER,
  // Die Felder des Kopfblocks PARTEIEN (Adressen Etappe 3) — sie stehen IM Kopfblock und
  // werden in einem zweiten Weg gespeichert (`setBelegParteien`). Ein Befund von dort muss
  // denselben Weg ins Bild finden wie einer am Titel.
  //
  // `purchaseOrderNumber` steht bewusst mit drin, obwohl der Auftrag das Feld gar nicht
  // zeigt (er führt die Nummer als `customerPurchaseOrderNumber` selbst): Zeigt der Server
  // einmal doch darauf, soll der Block wenigstens aufklappen. Die Kennung zeigt dann ins
  // Leere, und `zeigeFeld` verträgt das (Prüfung in `useKopffaltung`).
  ...PARTEIEN_KOPFFELDER,
  // Der Abschnitt ZAHLPLAN (Migration 026) — dieselbe Überlegung, siehe die Begründung an
  // `ANGEBOT_KOPFFELDER`. Die SERVER-Feldnamen tragen einen Ratenindex und werden VOR
  // dieser Tabelle gesondert behandelt (`zeigeAuftragsFehlerFeld`, `istZahlplanFeld`).
  ...ZAHLPLAN_KOPFFELDER,
};

/**
 * Der Befund der Feldprüfung: der Satz für den Bearbeiter UND das Feld, um das es geht —
 * bei seinem SERVERNAMEN, damit Masken- und Serverweg durch dieselbe Tabelle laufen
 * (siehe `AngebotBefund`, dieselbe Überlegung).
 *
 * Alle vier Pflichtangaben stehen im eingefalteten Kopfblock; eingeklappt sähe niemand
 * einen Satz an einem Feld, das gar nicht zu sehen ist.
 */
export type AuftragBefund = {
  meldung: string;
  feld: "companyId" | "title" | "netAmount" | "orderedOn";
};

/** Die gemeinsame Prüfung für Anlegen und Bearbeiten (null = in Ordnung). */
export function pruefeAuftrag(
  entwurf: AuftragEntwurf, brauchtCompany: boolean,
): AuftragBefund | null {
  if (brauchtCompany && entwurf.companyId === "") {
    return { meldung: "Bitte ein Unternehmen wählen.", feld: "companyId" };
  }
  if (entwurf.title.trim() === "") {
    return { meldung: "Bitte einen Titel angeben.", feld: "title" };
  }
  if (Number.isNaN(betragZahl(entwurf.netAmount))) {
    return { meldung: "Bitte einen gültigen Betrag angeben.", feld: "netAmount" };
  }
  if (entwurf.orderedOn.trim() === "") {
    return { meldung: "Bitte das Bestelldatum angeben.", feld: "orderedOn" };
  }
  return null;
}

/** Den Kopfblock auf das Feld öffnen, das ein Befund der MASKE nennt. */
export function zeigeAuftragsFeld(
  kopf: Kopffaltung, praefix: string, feld: string,
): void {
  const ziel = kopffeldId(AUFTRAG_KOPFFELDER, praefix, feld);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

/** Dasselbe für einen Befund des SERVERS (422 mit `ApiError.fields[]`). Trägt der Fehler
 *  keine Befunde oder nur Felder ausserhalb des Blocks, bleibt der Block zu. */
export function zeigeAuftragsFehlerFeld(
  kopf: Kopffaltung, praefix: string, fehler: unknown,
): void {
  // Ein Befund am ZAHLPLAN trägt einen Ratenindex (`raten[2].sharePercent`) und passt in
  // keine feste Tabelle — `istZahlplanFeld` erkennt ihn VOR dem Tabellen-Nachschlag.
  if (fehler instanceof ApiError
      && (fehler.fields ?? []).some((f) => istZahlplanFeld(f.field))) {
    kopf.zeigeFeld(`${praefix}-zahlplan`);
    return;
  }
  const ziel = kopffeldAusFehler(AUFTRAG_KOPFFELDER, praefix, fehler);
  if (ziel !== null) kopf.zeigeFeld(ziel);
}

export type AuftragKopffelder = {
  title: string; netAmount: number; contactId: string | null;
  responsibleUserId: string | null; paymentTermId: string | null;
  customerPurchaseOrderNumber: string | null; orderedOn: string;
  startDate: string | null; endDate: string | null; openEnded: boolean;
  introText: string | null; outroText: string | null;
  termValue: number | null; termUnit: "MONTHS" | "YEARS" | null;
  minimumTermValue: number | null; minimumTermUnit: "MONTHS" | "YEARS" | null;
  renewalPeriodValue: number | null; renewalPeriodUnit: "MONTHS" | "YEARS" | null;
  noticePeriodValue: number | null;
  noticePeriodUnit: "MONTHS" | "YEARS" | null;
  noticeReference: "TERM_END" | "CALENDAR_YEAR_END" | null;
  periodBasis: "CALENDAR_YEAR" | "CONTRACT_YEAR";
};

/** Die Kopf- und Laufzeitfelder, die Anlegen und Ändern teilen (companyId/offerId gehören nur
 *  zum Anlegen — der Server lässt die Herkunft beim Ändern nicht zu). openEnded und endDate
 *  sind konsistent: ist unbefristet gesetzt, geht das Endedatum als null mit. */
export function auftragKopffelder(entwurf: AuftragEntwurf): AuftragKopffelder {
  /** Ein Wert/Einheit-PAAR für den Server: leer heisst BEIDES null. Ausgeschrieben und
   *  nicht gestreut, weil `exactOptionalPropertyTypes` ein `undefined` anders behandelt
   *  als einen fehlenden Schlüssel. */
  function paar(wert: string, einheit: "MONTHS" | "YEARS") {
    const kern = wert.trim();
    return kern === ""
      ? { wert: null, einheit: null }
      : { wert: Number(kern), einheit };
  }
  const term = paar(entwurf.termValue, entwurf.termUnit);
  const mindest = paar(entwurf.minimumTermValue, entwurf.minimumTermUnit);
  const verlaengerung = paar(entwurf.renewalPeriodValue, entwurf.renewalPeriodUnit);
  const frist = paar(entwurf.noticePeriodValue, entwurf.noticePeriodUnit);
  return {
    title: entwurf.title.trim(),
    // Mit Positionen ist der Betrag berechnet; das Feld zeigt ihn nur an und schickt ihn
    // unverändert zurück (der Server nimmt genau diesen Wert an, siehe gepruefterBetrag).
    netAmount: betragZahl(entwurf.netAmount),
    contactId: oderNull(entwurf.contactId),
    responsibleUserId: oderNull(entwurf.responsibleUserId),
    paymentTermId: oderNull(entwurf.paymentTermId),
    customerPurchaseOrderNumber: oderNull(entwurf.customerPurchaseOrderNumber),
    orderedOn: entwurf.orderedOn,
    startDate: oderNull(entwurf.startDate),
    endDate: entwurf.openEnded ? null : oderNull(entwurf.endDate),
    openEnded: entwurf.openEnded,
    introText: oderNull(entwurf.introText),
    outroText: oderNull(entwurf.outroText),
    termValue: term.wert, termUnit: term.einheit,
    minimumTermValue: mindest.wert, minimumTermUnit: mindest.einheit,
    renewalPeriodValue: verlaengerung.wert, renewalPeriodUnit: verlaengerung.einheit,
    noticePeriodValue: frist.wert, noticePeriodUnit: frist.einheit,
    // Der BEZUGSPUNKT hängt weiter an der Frist: Ohne Zahl gibt es keinen Stichtag.
    noticeReference: frist.wert === null ? null : entwurf.noticeReference,
    periodBasis: entwurf.periodBasis,
  };
}

/** Die Werte für die Platzhalter-Vorschau — genau das, was die Ansicht ohnehin geladen hat.
 *  Was fehlt, erscheint gar nicht erst und bleibt als sichtbares Token stehen. */
export function auftragVorschauWerte(arg: {
  entwurf: AuftragEntwurf;
  companies: Unternehmen[];
  kontakte: Ansprechpartner[];
  kundeName?: string | undefined;
}): Record<string, string> {
  const { entwurf, companies, kontakte, kundeName } = arg;
  const werte: Record<string, string> = {
    ...laufzeitVorschauWerte({
      termValue: entwurf.termValue, termUnit: entwurf.termUnit,
      minimumTermValue: entwurf.minimumTermValue, minimumTermUnit: entwurf.minimumTermUnit,
      renewalPeriodValue: entwurf.renewalPeriodValue,
      renewalPeriodUnit: entwurf.renewalPeriodUnit,
      noticePeriodValue: entwurf.noticePeriodValue,
      noticePeriodUnit: entwurf.noticePeriodUnit,
      noticeReference: entwurf.noticeReference,
    }),
    "datum.heute": formatDatum(new Date().toISOString()),
  };
  if (kundeName !== undefined && kundeName !== "") werte["kunde.name"] = kundeName;
  const firma = companies.find((c) => c.id === entwurf.companyId);
  if (firma !== undefined) werte["firma.name"] = firma.name;
  const kontakt = kontakte.find((k) => k.id === entwurf.contactId);
  if (kontakt !== undefined) {
    werte["ansprechpartner.name"] = kontakt.anzeigename;
    // Migration 025: die Briefanrede steht NEBEN dem Namen (masterdataRules.ts) — sie ist
    // eine Ableitung aus Geschlecht, Titel und Namen, kein eigenes Eingabefeld.
    werte["ansprechpartner.anrede"] = kontakt.briefanrede;
  }
  return werte;
}

export type AuftragFelderArgs = {
  praefix: string;
  mitUnternehmen: boolean;
  companies: Unternehmen[];
  umfeld: AuftraegeUmfeldDaten;
  entwurf: AuftragEntwurf;
  setEntwurf: Dispatch<SetStateAction<AuftragEntwurf>>;
  /** Hat der Vorgang Positionen? Dann ist der Nettobetrag berechnet und nur noch Anzeige. */
  hatPositionen: boolean;
  feldFehler: string | null;
  vorschauWerte: Record<string, string>;
  /** Der Griff auf die Einfaltung des Kopfblocks — er gehört der MASKE (siehe
   *  components/Kopffaltung.tsx). */
  kopf: Kopffaltung;
  /** Der POSITIONSBLOCK zwischen Einleitungs- und Ausleitungstext; beim Anlegen leer, weil
   *  es den Vorgang, an dem die Positionen hängen, dort noch nicht gibt (siehe
   *  `AngebotFelderArgs.positionen` — dieselbe Bauart, damit beide Vorgangsarten dieselbe
   *  Anordnung tragen). */
  positionen?: ReactNode | undefined;
  /** Der Kopfblock PARTEIEN — als Schlitz und aus demselben Grund wie `positionen`: Er
   *  hängt am geladenen Vorgang, und beim ANLEGEN gibt es den noch nicht. */
  parteien?: ReactNode | undefined;
  /** Der Abschnitt ZAHLUNGSVEREINBARUNG — als Schlitz aus demselben Grund wie `parteien`. */
  zahlplan?: ReactNode | undefined;
  /**
   * Die Periodenregel ist GESPERRT (Festlegung Z10): Dieser Auftrag wird aus einem
   * Angebot abgeleitet, und Periodenregel und Raten sind EINE Vereinbarung — sie kommt
   * aus dem Angebot, nicht aus einer zweiten, im Anlegeformular getroffenen Wahl. Nur die
   * ANLAGE kennt diesen Fall (`useAuftragAnlegen`); am gespeicherten Auftrag ist das Feld
   * gewöhnlich änderbar, deshalb ist die Vorgabe `false`.
   */
  periodBasisGesperrt?: boolean;
};

/**
 * Die Felder von Anlege- und Bearbeitungsmaske. Bewusst eine Funktion, die JSX ZURÜCKGIBT, und
 * keine eigene Komponente: eine im Render definierte Komponente bekäme bei jedem Neuzeichnen
 * eine neue Identität — React hängte ihren Teilbaum ab und neu auf, und der Fokus spränge nach
 * jedem getippten Zeichen aus dem Feld (Lehre aus KundeAnsprechpartner/KundeAngebote). Sie ist
 * auch nach dem Herauslösen des Bausteins gemeinsam: Der Rahmen zeichnet mit ihr seine
 * ANLAGE, das Detail sein Bearbeiten.
 *
 * **Die Reihenfolge** ist dieselbe wie am Angebot (Entscheid Dirk 08.09.2026, „Positionen
 * mit Präsenz"): eingefalteter Kopfblock, Einleitungstext, die POSITIONEN, der
 * Ausleitungstext. Eine Notiz führt der Auftrag nicht — der Block darüber trägt hier also
 * nur den einen Text.
 */
export function auftragFormularFelder(a: AuftragFelderArgs): ReactElement {
  const { praefix, umfeld, entwurf, setEntwurf, hatPositionen } = a;
  function setze(feld: keyof AuftragEntwurf) {
    return (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setEntwurf((v) => ({ ...v, [feld]: e.target.value }));
  }
  return (
    <div className="kauf__maske">
      <Kopfblock faltung={a.kopf} titel={entwurf.title}
        betragLabel={nettobetragLabel(hatPositionen)}
        betrag={kopfBetragText(entwurf.netAmount)}
        inhaltId={`${praefix}-kopfdaten`}>
        {auftragKopffeldRaster(a, setze)}
        {a.parteien}
        {a.zahlplan}
      </Kopfblock>

      {/* Einleitungstext: Rohtext mit Tokens, darunter die leichte Vorschau. Beides kommt
          aus der wiederverwendeten Editor-Komponente — in dieser Ansicht nachgebaut, gäbe
          es die Vorschau zweimal. */}
      <div className="kauf__texte">
        <PlatzhalterTextfeld id={`${praefix}-intro`} label="Einleitungstext"
          wert={entwurf.introText}
          onChange={(wert) => setEntwurf((v) => ({ ...v, introText: wert }))}
          variablen={umfeld.platzhalter} werte={a.vorschauWerte} />
      </div>

      {a.positionen}

      {/* Der Ausleitungstext schliesst den Beleg ab — also steht er hinter dem, was er
          abschliesst. Seine Hinweiszeilen bleiben an IHM: Seit die Positionen dazwischen
          stehen, wäre eine gemeinsame Fussnote für beide Textfelder eine Fussnote, die zu
          einem Feld gehört, das man von ihr aus nicht mehr sieht. */}
      <div className="kauf__texte">
        <PlatzhalterTextfeld id={`${praefix}-outro`} label="Ausleitungstext"
          wert={entwurf.outroText}
          onChange={(wert) => setEntwurf((v) => ({ ...v, outroText: wert }))}
          variablen={umfeld.platzhalter} werte={a.vorschauWerte} />
      </div>

      {/* Der Feldbefund steht am Ende der Maske, dicht über der Fußleiste: Dort wurde
          „Speichern" gedrückt, und dort wird er gesucht. Betrifft er ein Feld im
          eingeklappten Kopfblock, hat die Maske ihn bereits aufgeklappt und den Fokus
          hineingesetzt (siehe Kopffaltung.zeigeFeld). */}
      {a.feldFehler !== null && (
        <p className="kauf__feldfehler" role="alert">{a.feldFehler}</p>
      )}
    </div>
  );
}

/** Das zweispaltige Raster der KOPFFELDER — der Inhalt des einfaltbaren Blocks. Eigene
 *  Funktion allein der Lesbarkeit halber: Sie hat keinen eigenen Zustand und keine eigene
 *  Identität (kein Komponententyp), zeichnet also weiterhin im selben Zug wie die Maske. */
function auftragKopffeldRaster(
  a: AuftragFelderArgs,
  setze: (feld: keyof AuftragEntwurf) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
): ReactElement {
  const { praefix, companies, umfeld, entwurf, setEntwurf, hatPositionen } = a;
  return (
    <div className="kauf__felder">
      {a.mitUnternehmen && (
        <div className="kauf__feld">
          <label htmlFor={`${praefix}-company`} className="kauf__label">Unternehmen</label>
          <select id={`${praefix}-company`} className="kauf__select"
            value={entwurf.companyId} onChange={setze("companyId")}>
            {companies.length !== 1 && <option value="">Bitte wählen …</option>}
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.shortCode} · {c.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-title`} className="kauf__label">Titel</label>
        <input id={`${praefix}-title`} className="kauf__eingabe" type="text"
          value={entwurf.title} onChange={setze("title")} />
      </div>

      {/* Sobald der Vorgang Positionen hat, ist der Betrag BERECHNET: Das Feld zeigt ihn
          nur noch an. Von Hand gesetzt wäre er eine zweite Wahrheit — der Server weist ihn
          als Feldfehler ab (BETRAG_BERECHNET). `readOnly` statt `disabled`: Der Wert bleibt
          lesbar, kopierbar und geht beim Speichern unverändert mit (genau das erwartet der
          Server). */}
      <div className="kauf__feld">
        <label htmlFor={`${praefix}-amount`} className="kauf__label">
          {nettobetragLabel(hatPositionen)}
        </label>
        <input id={`${praefix}-amount`} className="kauf__eingabe" type="text"
          inputMode="decimal" value={entwurf.netAmount} onChange={setze("netAmount")}
          readOnly={hatPositionen} aria-readonly={hatPositionen} />
      </div>

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-ordered`} className="kauf__label">Bestellt am</label>
        <input id={`${praefix}-ordered`} className="kauf__eingabe" type="date"
          value={entwurf.orderedOn} onChange={setze("orderedOn")} />
      </div>

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-po`} className="kauf__label">Kunden-Bestellnummer</label>
        <input id={`${praefix}-po`} className="kauf__eingabe" type="text"
          value={entwurf.customerPurchaseOrderNumber}
          onChange={setze("customerPurchaseOrderNumber")} />
      </div>

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-payterm`} className="kauf__label">Zahlungskondition</label>
        <select id={`${praefix}-payterm`} className="kauf__select"
          value={entwurf.paymentTermId} onChange={setze("paymentTermId")}>
          <option value="">— keine —</option>
          {umfeld.konditionen.map((k) => (
            <option key={k.id} value={k.id}>{k.name}</option>
          ))}
        </select>
      </div>

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-contact`} className="kauf__label">Ansprechpartner</label>
        <select id={`${praefix}-contact`} className="kauf__select"
          value={entwurf.contactId} onChange={setze("contactId")}>
          <option value="">— keiner —</option>
          {umfeld.kontakte.map((k) => (
            <option key={k.id} value={k.id}>{k.anzeigename}</option>
          ))}
        </select>
      </div>

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-user`} className="kauf__label">Verantwortlich</label>
        <select id={`${praefix}-user`} className="kauf__select"
          value={entwurf.responsibleUserId} onChange={setze("responsibleUserId")}>
          <option value="">— niemand —</option>
          {umfeld.benutzer.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>

      <div className="kauf__feld">
        <label htmlFor={`${praefix}-start`} className="kauf__label">Laufzeitbeginn</label>
        <input id={`${praefix}-start`} className="kauf__eingabe" type="date"
          value={entwurf.startDate} onChange={setze("startDate")} />
      </div>

      {/* Der Unbefristet-Schalter blendet das Endedatum aus — dieselbe Konsistenz, die der
          Server erzwingt (openEnded ⇒ endDate = null). */}
      <div className="kauf__feld kauf__feld--schalter">
        <label className="kauf__schalter">
          <input id={`${praefix}-open`} type="checkbox" checked={entwurf.openEnded}
            onChange={(e) => setEntwurf((v) => ({ ...v, openEnded: e.target.checked }))} />
          Unbefristet
        </label>
      </div>

      {!entwurf.openEnded && (
        <div className="kauf__feld">
          <label htmlFor={`${praefix}-end`} className="kauf__label">Laufzeitende</label>
          <input id={`${praefix}-end`} className="kauf__eingabe" type="date"
            value={entwurf.endDate} onChange={setze("endDate")} />
        </div>
      )}

      {/* Die Periodenregel des Zahlplans (Migration 026) — beim Ableiten aus einem
          Angebot GESPERRT (Festlegung Z10): Periodenregel und Raten sind EINE
          Vereinbarung, und sie kommt dann aus dem Angebot. */}
      <div className="kauf__feld">
        <label htmlFor={`${praefix}-periodbasis`} className="kauf__label">
          Leistungszeitraum
        </label>
        <select id={`${praefix}-periodbasis`} className="kauf__select"
          value={entwurf.periodBasis} disabled={a.periodBasisGesperrt === true}
          onChange={setze("periodBasis")}>
          <option value="CALENDAR_YEAR">Kalenderjahr</option>
          <option value="CONTRACT_YEAR">Vertragsjahr</option>
        </select>
        {a.periodBasisGesperrt === true
          ? <p className="kauf__hinweis">Kommt aus dem Angebot.</p>
          : (
            <p className="kauf__hinweis">
              Bei Kalenderjahr zählen erstes und letztes Jahr VOLL — Beträge und
              Rechnungen werden für das ganze Kalenderjahr aufgenommen.
            </p>
          )}
      </div>

      {/* DIE LAUFZEIT (Migration 031, Entscheid E1): vier Dauern, jede als Zahl PLUS
          Einheit — vorher (Aufgabe 2) nur zwei nackte Monatszahlen und eine Frist ohne
          Vertragslaufzeit. Die Vertragslaufzeit steht zuerst — sie ist die Aussage, die
          ein Kunde als erste sucht, und die Spec nennt sie ausdrücklich als erstes Feld.
          Zahl und Einheit stehen NEBENEINANDER in einem Feldpaar: Zwei Zeilen wären zwei
          Angaben, und eine Einheit ohne ihre Zahl ist keine. Am Auftrag ist die Laufzeit
          das VEREINBARTE. */}
      <div className="kauf__feld kauf__feld--paar">
        <label htmlFor={`${praefix}-term`} className="kauf__label">Vertragslaufzeit</label>
        <div className="kauf__paar">
          <input id={`${praefix}-term`} className="kauf__eingabe kauf__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.termValue}
            onChange={setze("termValue")} />
          <select id={`${praefix}-term-unit`} className="kauf__select"
            aria-label="Einheit der Vertragslaufzeit"
            value={entwurf.termUnit} onChange={setze("termUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      <div className="kauf__feld kauf__feld--paar">
        <label htmlFor={`${praefix}-minterm`} className="kauf__label">Mindestlaufzeit</label>
        <div className="kauf__paar">
          <input id={`${praefix}-minterm`} className="kauf__eingabe kauf__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.minimumTermValue}
            onChange={setze("minimumTermValue")} />
          <select id={`${praefix}-minterm-unit`} className="kauf__select"
            aria-label="Einheit der Mindestlaufzeit"
            value={entwurf.minimumTermUnit} onChange={setze("minimumTermUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      <div className="kauf__feld kauf__feld--paar">
        <label htmlFor={`${praefix}-renewal`} className="kauf__label">Verlängerung</label>
        <div className="kauf__paar">
          <input id={`${praefix}-renewal`} className="kauf__eingabe kauf__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.renewalPeriodValue}
            onChange={setze("renewalPeriodValue")} />
          <select id={`${praefix}-renewal-unit`} className="kauf__select"
            aria-label="Einheit der Verlängerung"
            value={entwurf.renewalPeriodUnit} onChange={setze("renewalPeriodUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      <div className="kauf__feld kauf__feld--paar">
        <label htmlFor={`${praefix}-notice`} className="kauf__label">Kündigungsfrist</label>
        <div className="kauf__paar">
          <input id={`${praefix}-notice`} className="kauf__eingabe kauf__eingabe--zahl"
            type="number" min={0} inputMode="numeric" value={entwurf.noticePeriodValue}
            onChange={setze("noticePeriodValue")} />
          <select id={`${praefix}-notice-unit`} className="kauf__select"
            aria-label="Einheit der Kündigungsfrist"
            value={entwurf.noticePeriodUnit} onChange={setze("noticePeriodUnit")}>
            <option value="MONTHS">Monate</option>
            <option value="YEARS">Jahre</option>
          </select>
        </div>
      </div>

      {/* Der Bezugspunkt „Kündigung wirkt" bleibt UNVERÄNDERT an seiner Stelle hinter der
          Frist — er ist kein eigenes Wert/Einheit-Paar, sondern die Wahl, worauf sich die
          Frist bezieht. */}
      <div className="kauf__feld kauf__feld--breit">
        <label htmlFor={`${praefix}-notice-ref`} className="kauf__label">
          Kündigung wirkt
        </label>
        <select id={`${praefix}-notice-ref`} className="kauf__select"
          value={entwurf.noticeReference} onChange={setze("noticeReference")}>
          <option value="TERM_END">zum Ende der Laufzeit</option>
          <option value="CALENDAR_YEAR_END">zum Ende des Kalenderjahres</option>
        </select>
      </div>

    </div>
  );
}

export type AuftragDetailProps = {
  client: AuftragDetailClient;
  kundeId: string;
  /** Der Vorgang — fertig geladen, samt seiner `version`. Wie am Angebot bewusst der SATZ
   *  und nicht bloss die Kennung: Der Rahmen führt seine Liste ohnehin, ein Nachladen je
   *  Zeile wäre eine Verhaltensänderung. */
  auftrag: Auftrag;
  /** Die Unternehmen mit Kundenbeziehung — für das Kürzel und die Platzhalter-Vorschau. */
  companies: Unternehmen[];
  /** Die vom Rahmen geladenen Auswahllisten (`useAuftraegeUmfeld`). */
  umfeld: AuftraegeUmfeldDaten;
  /** Steht GENAU DIESER Vorgang gerade in Bearbeitung? */
  bearbeitung: boolean;
  /** Ist die Bedienung gesperrt, weil anderswo ein Formular offen ist? */
  gesperrt: boolean;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`). */
  kundeName?: string | undefined;
  /** Ist die Sitzung ADMIN? Allein für das Lösen von Abrechnungsquellen an nicht aktiven
   *  Aufträgen (Entscheid Dirk 29.09.2026, Punkt 7). Weglassbar — dann nicht; den echten
   *  Riegel trägt die Route. */
  istAdmin?: boolean | undefined;
  onBearbeiten: () => void;
  onSchliessen: () => void;
  /** Der Rahmen soll seine Liste neu holen (nach Statuswechsel, Speichern, 409). */
  onNeuLaden: () => void | Promise<unknown>;
  /** Der Meldungsstreifen gehört dem Rahmen — er steht über der ganzen Ansicht. */
  onHinweis: (text: string | null) => void;
  /** Der Vorgang ist weitergedreht (Version/Betrag aus dem Positions-Editor): Der Rahmen
   *  schreibt ihn in seine Liste fort und reicht ihn als `auftrag` zurück herein. Ohne das
   *  rechneten die Statusknöpfe mit einer veralteten Version. */
  onFortschreiben: (auftrag: Auftrag) => void;
  /**
   * Die Auftrag→Rechnung-Naht (Paket 3b-2). Ist sie gesetzt, erscheint „Rechnung erzeugen":
   * Der Baustein LEITET die Rechnung hier ab (`createInvoiceFromOrder`) und meldet den
   * fertigen Entwurf nach oben; der Rahmen wechselt daraufhin in den Rechnungen-Reiter.
   *
   * Warum die Ableitung HIER geschieht und nicht im Ziel (anders als bei Angebot→Auftrag):
   * Dort muss der Bearbeiter noch Felder ausfüllen, das Ziel bekommt also eine Vorbelegung
   * für seine Maske. Hier ist es ein einziger Serveraufruf ohne Eingabe — und wenn er
   * scheitert, soll die Meldung AM AUFTRAG stehen, nicht in einem frisch aufgeschlagenen,
   * leeren Rechnungen-Reiter.
   *
   * PFLICHT-Eigenschaft, obwohl sie `undefined` sein darf (Muster `onFortschreiben`): Wer
   * die Naht bewusst nicht verdrahtet, schreibt `undefined` hin und sieht dabei, dass er
   * einen Knopf abwählt. Wäre sie weglassbar, fehlte der Knopf nach einem Vergessen in
   * Aufgabe 4 still — und still fehlende Knöpfe fallen erst dem Bearbeiter auf.
   */
  onRechnungAusAuftrag: ((rechnung: Rechnung) => void) | undefined;
};

/**
 * Das Präfix der Feld-Kennungen dieser Maske — einmal benannt, weil daraus auch das Ziel
 * des Auto-Aufklappens gebildet wird (`kopf.zeigeFeld`).
 */
const BEARBEITEN_PRAEFIX = "kauf-bearbeiten";

export function AuftragDetail(p: AuftragDetailProps): ReactElement {
  const { client, kundeId, auftrag, companies, umfeld, bearbeitung, gesperrt } = p;

  const [entwurf, setEntwurf] = useState<AuftragEntwurf>(() => auftragEntwurfAus(auftrag));
  /**
   * Die Einfaltung der Kopffelder: Beim BEARBEITEN eines bestehenden Vorgangs beginnt sie
   * ZU (Entscheid Dirk 08.09.2026) — die Arbeit findet an den Positionen statt, die
   * dadurch nach oben rücken.
   */
  const kopf = useKopffaltung(false);
  /** Die Einfaltung des LESENDEN Kastens „Zahlungsvereinbarung und Fälligkeiten" im
   *  geschlossenen Detail (Gesamtreview, I2) — VORGABE geschlossen: Ohne sie riefen
   *  `getZahlplan` UND `getFaelligkeiten` sofort ab, obwohl kaum je jemand hinsieht. */
  const faelligkeitenFaltung = useKopffaltung(false);
  /** Die Einfaltung des ZWEITEN lesenden Kastens „Abrechnung und Volumen" (Paket
   *  Teilrechnungen) — ein EIGENER Aufklapper neben „Zahlungsvereinbarung und
   *  Fälligkeiten", nicht darin: Beide Paare gehören fachlich zusammen, aber der
   *  bestehende Aufklapper wird nicht umbenannt oder erweitert (E2E und Tests hängen an
   *  seinem Titel) — und ein Auftragsdetail mit VIER Lesekästen unter EINEM Titel liefe
   *  in eine Wand aus Kästen. VORGABE geschlossen, aus demselben Grund wie dort: Sonst
   *  riefen `getAbrechnung` UND `getVolumen` sofort ab, obwohl kaum je jemand hinsieht. */
  const abrechnungFaltung = useKopffaltung(false);
  /** Die Einfaltung des LESENDEN Kastens „Anhänge" — dieselbe Vorgabe geschlossen wie die
   *  beiden Kästen darüber (Gesamtreview, I2): `Anhaenge` holt in seinem Effekt sofort
   *  `getAnhaenge` ab, sobald es gezeichnet wird — bei vielen Aufträgen in der Liste wäre
   *  das ein Abruf je Zeile, den fast niemand ansieht. */
  const anhaengeFaltung = useKopffaltung(false);
  /** Der Kasten „Abrechnungsquellen" (Spec, Entscheid 6) — VORGABE geschlossen wie die
   *  Nachbarn, AUSSER der Auftrag ist gestört: Dann klappt er vorbelegt auf (Spec: „Der
   *  Kasten selbst klappt in diesem Fall vorbelegt auf"). Ein geschlossener Kasten fragt
   *  nicht nach (kein Abruf je Listenzeile). */
  const quellenFaltung = useKopffaltung(auftrag.abrechnungsquellenStoerung === true);
  /** Die zuletzt geladene Sicht — sie macht aus der Störungszeile den genauen Satz. */
  const [quellenSicht, setQuellenSicht] = useState<Abrechnungsquellen | null>(null);
  /** Gestört? Mit geladener Sicht aus ihr, sonst aus dem Listenfeld (Ruling R-FLAG).
   *  Die Sicht zählt nur, solange sie zur Auftragsversion PASST: Ein Speichern bei
   *  geschlossenem Kasten (Zahlplan, Status) dreht die Version weiter, und der Rahmen
   *  bringt mit dem frischen Auftrag das frische Listenfeld — eine alte Sicht darf es
   *  dann nicht überstimmen. Jede Sicht aus dem Kasten schreibt ihre Version über
   *  `quellenGeladen` zurück, passt also, solange nichts anderes geschehen ist. */
  const quellenSichtGilt = quellenSicht !== null && quellenSicht.version === auftrag.version;
  const quellenStoerung = quellenSichtGilt
    ? hatQuellenStoerung(quellenSicht) : auftrag.abrechnungsquellenStoerung === true;
  const quellenPraefix = `kauf-quellen-${auftrag.id}`;
  /** Steht das Vorschlags-Modal offen? (Paket Teilrechnungen, Knopf „Rechnung
   *  vorschlagen".) */
  const [vorschlagOffen, setVorschlagOffen] = useState(false);
  const [feldFehler, setFeldFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  /** Der Positionsstand des Vorgangs — der Editor meldet ihn nach jedem Schritt. */
  const [positionen, setPositionen] = useState<PositionsStand | null>(null);
  /** Läuft gerade eine Aktion an DIESEM Vorgang (sperrt seine Knöpfe)? */
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  /** Die Rückmeldung des „Als PDF"-Knopfes — MIT ihrer Art (siehe PDF_MELDUNGS_STIL). */
  const [pdfMeldung, setPdfMeldung] = useState<PdfMeldung | null>(null);
  /** Ist die Vorschau offen? Sie hält keinen eigenen Stand über das Schliessen hinaus —
   *  ein `boolean` genügt, die Bilder holt sie sich selbst (Spec). */
  const [vorschauOffen, setVorschauOffen] = useState(false);
  /** Der Entwurf des Kopfblocks PARTEIEN — Bauart und Begründung bei `useParteienGriff`
   *  (vorgangsdetail.ts). */
  const parteien = useParteienGriff();
  /** Der Entwurf des Abschnitts ZAHLUNGSVEREINBARUNG — DRITTER Speicherweg
   *  (`useZahlplanGriff`, vorgangsdetail.ts). */
  const zahlplan = useZahlplanGriff();

  /**
   * Der Übergang „schliesst auf": Beim Öffnen der Bearbeitung wird der Entwurf frisch aus
   * dem Vorgang gebildet, und der Stand des vorigen Anlaufs verfällt. Bewusst WÄHREND des
   * Zeichnens statt in einem Effekt (React: „Adjusting state when a prop changes") — ein
   * Effekt liefe erst nach dem ersten Bild. `setPositionen(null)` gehört dazu, sonst spränge
   * der Nettobetrag in die Nur-Lese-Fassung, bevor der Editor geladen hat.
   */
  const [zuletztBearbeitung, setZuletztBearbeitung] = useState(bearbeitung);
  if (bearbeitung !== zuletztBearbeitung) {
    setZuletztBearbeitung(bearbeitung);
    if (bearbeitung) {
      setEntwurf(auftragEntwurfAus(auftrag));
      setFeldFehler(null);
      setPositionen(null);
      parteien.zuruecksetzen();
      zahlplan.zuruecksetzen();
      // Und die Einfaltung mit: Wer ein Panel neu aufschlägt, findet den Kopfblock im
      // Startzustand vor — nicht so, wie er ihn beim vorigen Vorgang hinterlassen hat.
      kopf.zuruecksetzen();
      // Die Vorschau schliesst, wenn die Bearbeitung von AUSSEN beginnt (M5, Schlussrunde):
      // Ihr Schleier liegt bei z-index 60, das Vorschau-Modal bei 100 — ohne diese Zeile
      // läge das Bearbeiten-Panel HINTER der Vorschau, statt sie abzulösen.
      setVorschauOffen(false);
    }
  }

  /**
   * Der Editor meldet nach jedem Schritt die frische Version des VORGANGS und den ganzen
   * Stand. Beides muss ankommen: Die Version geht in das nächste `updateSalesOrder` (sonst
   * 409), und aus dem Stand liest die Maske den berechneten Betrag. Und zwar bis in die
   * LISTE des Rahmens (`onFortschreiben`) — dieselbe Begründung wie am Angebot: Die
   * Statusknöpfe arbeiten mit der Version aus der Liste, und wer Positionen bearbeitet und
   * dann „Abbrechen" drückt, bekäme sonst beim ersten Klick eine 409 auf eine Änderung, die
   * er selbst gemacht hat.
   */
  function positionenGeaendert(version: number, stand: PositionsStand): void {
    setPositionen(stand);
    p.onFortschreiben({ ...auftrag, version, netAmount: stand.netAmount });
    setEntwurf((v) => (
      // Sobald Positionen da sind, ist der Betrag berechnet — das Anzeigefeld führt genau
      // den Wert, den `updateSalesOrder` unverändert zurückerwartet.
      stand.items.length === 0 ? v : { ...v, netAmount: betragText(stand.netAmount) }));
  }

  /** Hat der Vorgang Positionen? Entschieden an der ANZAHL, nicht am Betrag: Ein Beleg aus
   *  lauter Zugaben zu 0,00 hat Positionen (Fund G1 aus Aufgabe 2). */
  const hatPositionen = positionen !== null && positionen.items.length > 0;

  /**
   * Der zweite Speicherweg im selben Klick — Reihenfolge, Version und Sinn stehen an
   * `speichereParteien` (vorgangsdetail.ts).
   *
   * **Die BESTELLNUMMER gewinnt aus dem Kopfentwurf** (Fixrunde 1, BLOCKER). Der Auftrag
   * führt sie als eigene Spalte, und der Block zeigt sie deshalb gar nicht — seinen
   * Entwurf trägt sie trotzdem, mit dem Wert VOM LADEN. `setBelegParteien` verlangt das
   * Feld im Rumpf (`null` heisst gelöscht, weglassen geht nicht), und der Schreibweg
   * synchronisiert es in die Spalte zurück. Ohne diese Übersteuerung überschriebe der
   * zweite Weg still die Nummer, die `updateSalesOrder` eine Zeile vorher gespeichert und
   * durchgeschrieben hat — die Eingabe wäre weg, ohne eine einzige Meldung.
   */
  function parteienSpeichern(frisch: Auftrag): Promise<number | null> {
    let version: number | null = null;
    return speichereParteien({
      client, art: "order", customerId: auftrag.customerId, vorgangId: auftrag.id,
      version: frisch.version, griff: parteien,
      ueberschreibt: {
        purchaseOrderNumber: oderNull(entwurf.customerPurchaseOrderNumber),
      },
      beiFehler: (meldung, fehler) => {
        // Der frische Vorgang muss in die Liste des Rahmens und in den Entwurf — sonst
        // rechnete der nächste Anlauf mit der alten Version und bekäme eine 409 über eine
        // Änderung, die niemand fremd gemacht hat.
        p.onFortschreiben(frisch);
        setEntwurf(auftragEntwurfAus(frisch));
        setFeldFehler(meldung);
        zeigeAuftragsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, fehler);
      },
      beiErfolg: (neueVersion) => { version = neueVersion; },
    }).then((ok) => (ok ? version ?? frisch.version : null));
  }

  /** Der DRITTE Speicherweg — die Zahlungsvereinbarung. Kopf → Parteien → Zahlplan; die
   *  Version ist die aus der ANTWORT des Parteien-Weges (`speichereZahlplan`,
   *  vorgangsdetail.ts). */
  function zahlplanSpeichern(version: number): Promise<boolean> {
    return speichereZahlplan({
      client, bezug: { art: "order", customerId: auftrag.customerId,
                       vorgangId: auftrag.id },
      version, griff: zahlplan,
      beiFehler: (meldung, fehler) => {
        setFeldFehler(meldung);
        zeigeAuftragsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, fehler);
      },
    });
  }

  async function speichern(): Promise<void> {
    const problem = pruefeAuftrag(entwurf, false);
    if (problem !== null) {
      setFeldFehler(problem.meldung);
      // Der Befund betrifft immer ein Feld im Kopfblock — der beim Bearbeiten zugeklappt
      // beginnt. Ohne dieses Aufklappen stünde die Meldung über einem Formular, in dem das
      // gemeinte Feld gar nicht zu sehen ist: ein unsichtbarer Fehler.
      zeigeAuftragsFeld(kopf, BEARBEITEN_PRAEFIX, problem.feld);
      return;
    }
    setSpeichert(true);
    setFeldFehler(null);
    try {
      // version = die gelesene Version: ein zwischenzeitlich fremd geänderter Satz ergibt 409
      // statt einer stillen Überschreibung.
      //
      // BINDEND FÜR VERWENDER: Die Liste darf nicht neu laden, solange ein Detail in
      // Bearbeitung ist — sonst würde eine Fremdänderung still überschrieben statt 409.
      // Die Version, die hier hinausgeht, ist die aus der Liste des Rahmens (`auftrag`);
      // ein Neuladen während der Bearbeitung ersetzte sie unbemerkt durch die fremde und
      // machte damit genau die optimistische Sperre wirkungslos, die diese Zeile aufruft.
      const frisch = await client.updateSalesOrder(
        kundeId, auftrag.id, auftrag.version, auftragKopffelder(entwurf));
      // Kopf → Parteien → Zahlplan. Jeder Weg dreht die Belegversion weiter; der nächste
      // bekommt die aus der ANTWORT des vorigen — der Zahlplan steht zuletzt, weil er der
      // einzige Weg ist, der die geplanten Fälligkeiten neu rechnet.
      const parteienVersion = await parteienSpeichern(frisch);
      if (parteienVersion === null) return;
      if (!await zahlplanSpeichern(parteienVersion)) return;
      p.onSchliessen();
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieser Auftrag wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen — bitte erneut bearbeiten.");
        p.onSchliessen();
        await p.onNeuLaden();
      } else {
        setFeldFehler(apiMeldung(e, "Die Änderung konnte nicht gespeichert werden."));
        // Die zweite Quelle von Feldbefunden: Der Server prüft mehr als die Maske —
        // Titel-Höchstlänge, Betragsgrenzen, Bestellnummer, halbe Kündigungsfrist,
        // BETRAG_BERECHNET und vor allem die Laufzeit-Konsistenz („Das Enddatum darf nicht
        // vor dem Startdatum liegen"), die es in der Maske gar nicht gibt. Ohne diese Zeile
        // stünde sein Befund über zwei eingeklappten Datumsfeldern.
        zeigeAuftragsFehlerFeld(kopf, BEARBEITEN_PRAEFIX, e);
      }
    } finally {
      setSpeichert(false);
    }
  }

  /**
   * „Rechnung erzeugen" — die Ableitung aus dem Auftrag (Paket 3b-2). Der Server kopiert
   * Kopf, Texte, Leistungszeitraum, den Zahlungssatz-Snapshot und die Positionen; hier wird
   * nichts zusammengestellt. Mehrere Rechnungen je Auftrag sind ausdrücklich erlaubt
   * (Abschlags-/Schlussrechnung), deshalb steht der Knopf auch an einem Auftrag, der schon
   * eine hat — und in JEDEM Status: Auch zu einem abgeschlossenen Auftrag darf eine
   * Nachrechnung entstehen.
   */
  async function rechnungErzeugen(): Promise<void> {
    const melden = p.onRechnungAusAuftrag;
    if (melden === undefined) return;
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      melden(await client.createInvoiceFromOrder(kundeId, auftrag.id));
    } catch (e) {
      p.onHinweis(apiMeldung(e, "Die Rechnung konnte nicht erzeugt werden."));
    } finally {
      setBeschaeftigt(false);
    }
  }

  async function statusSetzen(status: OrderStatus): Promise<void> {
    setBeschaeftigt(true);
    p.onHinweis(null);
    try {
      await client.setSalesOrderStatus(kundeId, auftrag.id, auftrag.version, status);
      await p.onNeuLaden();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        p.onHinweis("Dieser Auftrag wurde zwischenzeitlich geändert. Der aktuelle Stand ist "
          + "neu geladen.");
        await p.onNeuLaden();
      } else {
        p.onHinweis(apiMeldung(e, "Der Status konnte nicht geändert werden."));
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * „Als PDF" (E3-T4): Die Engine setzt die AUFTRAGSBESTÄTIGUNG zum Auftrag, die API
   * liefert den Download durch, hier wird er gespeichert. Die drei Ausgänge tragen ihre
   * ART mit (PDF_MELDUNGS_STIL): Erfolg als Status, die 501 der unkonfigurierten
   * Umgebung als HINWEIS (bekannter Arbeitsstand, kein Alarm), alles andere als Alarm.
   */
  async function alsPdf(): Promise<void> {
    setBeschaeftigt(true);
    setPdfMeldung(null);
    try {
      const { blob, filename } = await client.renderOrder(kundeId, auftrag.id);
      const name = filename ?? "auftragsbestaetigung.pdf";
      speichereBlob(blob, name);
      setPdfMeldung({ text: `Das PDF wurde heruntergeladen (${name}).`, art: "gut" });
    } catch (e) {
      if (e instanceof ApiError && e.status === 501) {
        setPdfMeldung({
          text: "Die Render-Engine ist noch nicht angebunden — es entsteht noch kein PDF.",
          art: "hinweis",
        });
      } else {
        // Nennt die Engine Feldpfade, reisen sie strukturiert mit (`fields[]`, an JEDEM
        // Engine-Fehlschlag) und werden unten als Liste gezeigt. Der Schlüssel entfällt,
        // wenn keine kamen — `exactOptionalPropertyTypes`.
        setPdfMeldung({
          text: apiMeldung(e, "Der Auftrag konnte nicht als PDF ausgegeben werden."),
          art: "schlecht",
          ...(e instanceof ApiError && e.fields !== undefined ? { felder: e.fields } : {}),
        });
      }
    } finally {
      setBeschaeftigt(false);
    }
  }

  /**
   * Trägt das Panel ungespeicherte Eingaben? Der Kopfblock PARTEIEN zählt mit: Er meldet
   * seinen Entwurf erst, sobald ihn jemand angefasst hat — und genau dann gibt es etwas zu
   * verlieren.
   */
  const ungespeichert = bearbeitung
    && (!entwuerfeGleich(entwurf, auftragEntwurfAus(auftrag))
      || parteien.beruehrt || zahlplan.beruehrt);

  /* Der Wächter gegen das Schliessen des BROWSERS (Global Constraint „kein stiller
     Datenverlust"): Der Abbrechen-Wächter fängt nur den Weg über den Knopf und Escape;
     ein geschlossener Reiter ginge an ihm vorbei. */
  useBeforeUnload(ungespeichert);

  const folgestatus = erlaubteFolgestatus(auftrag.status);
  const kuerzel = companies.find((c) => c.id === auftrag.companyId)?.shortCode ?? null;

  /**
   * Der EINE Ausgang des Bearbeitungspanels neben dem Speichern (Hausregel „striktes
   * Modalpanel", 06.09.2026) — dieselbe Bauart wie am Angebot: kein Ausgang unter laufendem
   * Schreiben, Rückfrage bei ungespeicherten Kopfeingaben, verglichen gegen den Vorgang, wie
   * er GERADE steht (der Positions-Editor dreht ihn selbst weiter). Die Positionen stehen
   * nie ungespeichert im Panel — der Editor schreibt jeden Schritt sofort.
   */
  function abbrechen(): void {
    if (speichert) return;
    if (!darfAbbrechen(ungespeichert)) return;
    parteien.zuruecksetzen();
    zahlplan.zuruecksetzen();
    p.onSchliessen();
  }

  /** Der Sprung in den Kasten — aus der Störungszeile und aus dem Rechnungsvorschlag.
   *  `zeigeFeld` klappt auf und fokussiert danach den Container (tabIndex -1). */
  function zuDenQuellen(): void {
    quellenFaltung.zeigeFeld(`${quellenPraefix}-kasten`);
  }

  /**
   * Eine neue Sicht aus dem Kasten (Fixrunde A10-1, Controller-Ruling zur Version):
   *
   * - Nach dem EIGENEN erfolgreichen Speichern (`ausSchreiben`) stempelt der Rahmen die neue
   *   Auftragsversion samt Störung in seine Liste — sonst liefe das nächste Speichern in eine
   *   409, und der Listen-Chip stünde still falsch. Das ist sicher: Der PUT ging gegen genau
   *   die Version, die der Rahmen kennt (der Kasten schickt `version={auftrag.version}`,
   *   nicht die seiner Sicht — Fixrunde A10-2), dazwischen kann niemand etwas geändert haben.
   *   Nach einer 409 bleibt das offene Modal stehen: Beide Rahmen (KundeAuftraege,
   *   BereichAuftraege) laden neu, ohne das Detail neu einzuhängen (Schlüssel = Auftrags-ID,
   *   kein Zwischenstand „Lädt"); der Kasten lädt mit der neuen Version nur seine Sicht nach.
   * - Eine GELESENE oder aus einem Konflikt stammende Sicht mit ANDERER Version heisst:
   *   Jemand anderes hat den Auftrag geändert. Ihre Version auf die alten Felder zu stempeln
   *   hiesse, das nächste „Bearbeiten → Speichern" überschriebe still die fremde Änderung.
   *   Deshalb lädt der Rahmen neu (`onNeuLaden`); bis dahin gilt das Listenfeld
   *   (`quellenSichtGilt` ist falsch).
   * - Eine gelesene Sicht mit GLEICHER Version darf nur das Störungsfeld nachführen (keine
   *   Version) — und nur bei einer Änderung, sonst zeichnete jedes Laden neu.
   */
  function quellenGeladen(s: Abrechnungsquellen, herkunft?: { ausSchreiben: true }): void {
    setQuellenSicht(s);
    const stoerung = hatQuellenStoerung(s);
    const stoerungAlt = auftrag.abrechnungsquellenStoerung === true;
    if (herkunft?.ausSchreiben === true) {
      if (s.version !== auftrag.version || stoerung !== stoerungAlt) {
        p.onFortschreiben({ ...auftrag, version: s.version, abrechnungsquellenStoerung: stoerung });
      }
      return;
    }
    if (s.version !== auftrag.version) {
      // Ein fehlschlagendes Neuladen meldet der Rahmen selbst; hier bleibt nur, dass es
      // nicht als unbehandelte Ablehnung durchschlägt.
      void Promise.resolve(p.onNeuLaden()).catch(() => undefined);
      return;
    }
    if (stoerung !== stoerungAlt) {
      p.onFortschreiben({ ...auftrag, abrechnungsquellenStoerung: stoerung });
    }
  }
  /** Die Laufzeit-Ende-Angabe: „unbefristet", ein Enddatum oder nichts. */
  const ende = auftrag.openEnded
    ? "unbefristet"
    : (auftrag.endDate !== null ? `läuft bis ${formatDatum(auftrag.endDate)}` : null);

  return (
    <>
      <div className="kauf__zeile">
        <span className="kauf__nummer">{auftrag.orderNumber}</span>
        {kuerzel !== null && <span className="kauf__kuerzel">{kuerzel}</span>}
        <span className="kauf__eintrag-titel">{auftrag.title}</span>
        {/* Schlechtestes oben (Hausregel): Der Chip steht VOR dem Statuschip. */}
        {quellenStoerung && <span className="kauf__quellenchip">Quelle fehlt</span>}
        <span className={`kauf__status kauf__status--${auftrag.status.toLowerCase()}`}>
          {AUFTRAG_STATUS_LABEL[auftrag.status]}
        </span>
      </div>

      <div className="kauf__daten">
        <span className="kauf__betrag ki-num">
          {formatBetrag(auftrag.netAmount, auftrag.currency)}
        </span>
        {ende !== null && <span className="kauf__laufzeit">{ende}</span>}
        {auftrag.sourceOfferNumber !== null && (
          <span className="kauf__herkunft">aus Angebot {auftrag.sourceOfferNumber}</span>
        )}
        {auftrag.contactName !== null && auftrag.contactName !== "" && (
          <span className="kauf__kontakt">{auftrag.contactName}</span>
        )}
      </div>

      {/* Die PDF-Rückmeldung steht AM VORGANG, zu dem sie gehört: Der Baustein trägt sie
          samt ihrer Befundliste selbst, damit sie auch dort erscheint, wo kein Reiter
          darüber liegt (der Auftrags-Bereich, views/BereichAuftraege.tsx). */}
      {pdfMeldung !== null && (
        <p className={PDF_MELDUNGS_STIL[pdfMeldung.art].klasse}
          role={PDF_MELDUNGS_STIL[pdfMeldung.art].rolle}>
          {pdfMeldung.text}
        </p>
      )}
      {/* Die Feldbefunde als LISTE — siehe die Begründung in AngebotDetail.tsx: neben der
          Meldung statt in ihr (die trägt `role="alert"` und bleibt ein Satz), und mit
          EIGENEM Blocknamen `kauf__` statt einer über die Ansichten geteilten Klasse. */}
      {pdfMeldung?.felder !== undefined && pdfMeldung.felder.length > 0 && (
        <ul className="kauf__fehlerfelder" aria-label="Fehlende Pflichtwerte">
          {pdfMeldung.felder.map((f) => (
            <li key={f.field}>
              <code>{f.field}</code>: {f.message}
            </li>
          ))}
        </ul>
      )}

      {bearbeitung && (
        <Modal titel={`Auftrag ${auftrag.orderNumber} bearbeiten`} breite="weit"
          onAbbrechen={abbrechen} abbrechenGesperrt={speichert}>
          <div className="kauf__bearbeitung">
            {auftragFormularFelder({
              praefix: BEARBEITEN_PRAEFIX, mitUnternehmen: false, companies, umfeld,
              entwurf, setEntwurf, hatPositionen, feldFehler, kopf,
              vorschauWerte: auftragVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, kundeName: p.kundeName,
              }),
              /* Der Kopfblock PARTEIEN — `key` am Vorgang: Ein Wechsel des bearbeiteten
                 Auftrags baut ihn neu auf, statt den Entwurf des vorigen weiterzureichen.
                 OHNE Bestellnummer: Die führt der Auftrag als eigenes Kopffeld
                 (`customerPurchaseOrderNumber`) schon einen Block weiter oben. */
              parteien: (
                <BelegParteien key={auftrag.id} client={client} art="order"
                  customerId={auftrag.customerId} vorgangId={auftrag.id}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={speichert}
                  mitBestellnummer={false}
                  befunde={parteien.befunde} onEntwurf={parteien.melde} />
              ),
              /* Der Abschnitt ZAHLUNGSVEREINBARUNG — `key` am Vorgang aus demselben Grund
                 wie an PARTEIEN: Ein Wechsel der Kennung (z. B. nach einer Ableitung) baut
                 den Abschnitt neu auf, statt fremde Ratenkennungen weiterzutragen. */
              zahlplan: (
                <Zahlplan key={`${auftrag.id}-zahlplan`} client={client}
                  bezug={{ art: "order", customerId: auftrag.customerId,
                           vorgangId: auftrag.id }}
                  idPraefix={BEARBEITEN_PRAEFIX} verdeckt={speichert}
                  befunde={zahlplan.befunde} onEntwurf={zahlplan.melde} />
              ),
              /* Der grösste Block, in der Mitte der Maske (Entscheid Dirk 08.09.2026).
                 DERSELBE Editor wie am Angebot — nur `art` unterscheidet die beiden
                 Einsatzorte. Die Positionen hängen an DEMSELBEN Vorgang wie die Kopffelder
                 darüber: Jeder Schritt im Editor dreht die Version des Auftrags weiter, und
                 `positionenGeaendert` trägt sie über den Rahmen zurück — sonst liefe das
                 nächste Speichern in eine 409. */
              positionen: (
                <div className="kauf__positionen">
                  <h3 className="kauf__abschnitt">Positionen</h3>
                  <PositionsEditor
                    client={client}
                    bezug={{ art: "order", customerId: kundeId, vorgangId: auftrag.id }}
                    companyId={auftrag.companyId} version={auftrag.version}
                    onVersionChange={positionenGeaendert} />
                </div>
              ),
            })}
          </div>

          {/* Die zwei einzigen Ausgänge, unten am Panel: Speichern schreibt die KOPFFELDER
              (die Positionen schreibt der Editor Schritt für Schritt selbst). */}
          <ModalFuss speichernText="Speichern" speichernGesperrt={speichert}
            abbrechenGesperrt={speichert}
            onSpeichern={() => { void speichern(); }} onAbbrechen={abbrechen} />
        </Modal>
      )}

      {!bearbeitung && (
        <div className="kauf__aktionen">
          <button type="button" className="kauf__sekundaer"
            aria-label={`${auftrag.title} bearbeiten`}
            disabled={gesperrt || beschaeftigt}
            onClick={p.onBearbeiten}>
            Bearbeiten
          </button>
          {/* Die Vorschau — DIREKT NEBEN „Als PDF" (Spec, wörtlich). Der Entwurf-Chip der
              Vorschau erscheint hier NIE: Die Engine antwortet an einer Auftragsbestätigung
              immer mit `entwurf: false` (sie kennt keinen Entwurf), und der Baustein liest
              genau dieses Feld — hier steht deshalb KEINE eigene Verzweigung dafür. */}
          <button type="button" className="kauf__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => setVorschauOffen(true)}>
            Vorschau
          </button>
          <button type="button" className="kauf__sekundaer"
            disabled={gesperrt || beschaeftigt}
            onClick={() => { void alsPdf(); }}>
            Als PDF
          </button>
          {folgestatus.map((s) => (
            <button key={s} type="button" className="kauf__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void statusSetzen(s); }}>
              {AUFTRAG_STATUS_LABEL[s]}
            </button>
          ))}
          {/* „Rechnung vorschlagen" (Paket Teilrechnungen) — nur an einem AKTIVEN Auftrag:
              An einem abgeschlossenen oder stornierten Auftrag entstehen keine weiteren
              geplanten Posten mehr, „Rechnung erzeugen" bleibt dort trotzdem für die
              Nachrechnung (siehe dessen Begründung). Dieselbe Fähigkeitsprüfung wie bei
              „Rechnung erzeugen": Wer die Naht zur Rechnung nicht verdrahtet hat, bekommt
              auch diesen Knopf nicht. */}
          {p.onRechnungAusAuftrag !== undefined && auftrag.status === "ACTIVE" && (
            <button type="button" className="kauf__sekundaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => setVorschlagOffen(true)}>
              Rechnung vorschlagen
            </button>
          )}
          {p.onRechnungAusAuftrag !== undefined && (
            <button type="button" className="kauf__primaer"
              disabled={gesperrt || beschaeftigt}
              onClick={() => { void rechnungErzeugen(); }}>
              Rechnung erzeugen
            </button>
          )}
        </div>
      )}

      {/* Das VORSCHLAGS-MODAL (Paket Teilrechnungen) — derselbe Sprung wie nach „Rechnung
          erzeugen": Der frische Entwurf geht über `onRechnungAusAuftrag` an den Rahmen,
          der auf den Rechnungen-Reiter wechselt. Ein eigener Naht-Rückruf nur für diesen
          Weg wäre eine zweite Tür in dieselbe Wand. */}
      {vorschlagOffen && p.onRechnungAusAuftrag !== undefined && (
        <Rechnungsvorschlag client={client} customerId={kundeId} orderId={auftrag.id}
          onAbbrechen={() => setVorschlagOffen(false)}
          onZuAbrechnungsquellen={() => { setVorschlagOffen(false); zuDenQuellen(); }}
          onFertig={(rechnung) => {
            setVorschlagOffen(false);
            p.onRechnungAusAuftrag?.(rechnung);
          }} />
      )}

      {/* DIE STÖRUNGSZEILE (Spec „Abrechnungsquellen", Oberfläche): über den Kästen, rot,
          mit Sprung in den Kasten. Sie steht nur am AKTIVEN Auftrag (R-STOERUNG). */}
      {!bearbeitung && quellenStoerung && (
        <p className="kauf__quellenstoerung" role="alert">
          <span>{stoerungsText(quellenSichtGilt ? quellenSicht : null)}</span>
          <button type="button" className="kauf__quellensprung"
            aria-label={`Zum Kasten Abrechnungsquellen von ${auftrag.orderNumber}`}
            onClick={zuDenQuellen}>
            Zum Kasten
          </button>
        </p>
      )}

      {/* Die LESENDEN Kästen ZAHLUNGSVEREINBARUNG und GEPLANTE FÄLLIGKEITEN — beide
          hinter EINEM Aufklapper „Zahlungsvereinbarung und Fälligkeiten" (Gesamtreview,
          I2, sanktionierte Vereinfachung): Eine Vereinbarung ohne ihre Fälligkeiten zu
          zeigen wäre die halbe Auskunft, und zwei Aufklapper für zwei Kästen, die
          ohnehin zusammen gelesen werden, wären eine Handbewegung zu viel. Geschlossen
          bleibt keiner der beiden Abrufe (`getZahlplan`/`getFaelligkeiten`) aus — bei
          vielen Aufträgen in der Liste wären das sonst zwei Abrufe je Zeile, für einen
          Kasten, den kaum je jemand aufklappt. Eigener Wrapper `kauf__faelligkeiten`
          allein für den Abstand an diesem Einbauort (kein geteilter Blockname). */}
      {!bearbeitung && (
        <div className="kauf__faelligkeiten">
          <Aufklapper faltung={faelligkeitenFaltung}
            titel="Zahlungsvereinbarung und Fälligkeiten"
            inhaltId="kauf-faelligkeiten-inhalt">
            {faelligkeitenFaltung.offen && (
              <>
                <Zahlplan client={client}
                  bezug={{ art: "order", customerId: kundeId, vorgangId: auftrag.id }}
                  idPraefix="kauf-lese" nurLesend />
                {/* Der Knopf „Neu berechnen" nur an einem AKTIVEN Auftrag (Gesamtreview,
                    I1): Der Server weist die Neuberechnung an einem stornierten oder
                    abgeschlossenen Auftrag mit 422 AUFTRAG_NICHT_AKTIV ab — derselbe
                    Gedanke wie beim Bearbeiten-Knopf in AngebotDetail. */}
                <GeplanteFaelligkeiten client={client} customerId={kundeId}
                  orderId={auftrag.id} currency={auftrag.currency}
                  neuBerechnenErlaubt={auftrag.status === "ACTIVE"} />
              </>
            )}
          </Aufklapper>
        </div>
      )}

      {/* Der Kasten „Abrechnungsquellen" — direkt unter „Zahlungsvereinbarung und
          Fälligkeiten" (Entscheid 6). Eigener Wrapper `kauf__quellen` allein für den
          Abstand an diesem Einbauort; der Baustein bringt seinen Block `kaq` selbst mit. */}
      {!bearbeitung && (
        <div className="kauf__quellen">
          <Aufklapper faltung={quellenFaltung} titel="Abrechnungsquellen"
            inhaltId={`${quellenPraefix}-inhalt`}>
            {quellenFaltung.offen && (
              <AbrechnungsquellenKasten client={client} customerId={kundeId}
                orderId={auftrag.id} version={auftrag.version} idPraefix={quellenPraefix}
                onSicht={quellenGeladen} istAdmin={p.istAdmin === true} />
            )}
          </Aufklapper>
        </div>
      )}

      {/* Die LESENDEN Kästen ABRECHNUNG und VOLUMEN (Paket Teilrechnungen) — ein ZWEITER,
          eigener Aufklapper direkt unter dem bestehenden (siehe dessen Begründung oben):
          Der bestehende Titel „Zahlungsvereinbarung und Fälligkeiten" bleibt unangetastet
          (E2E und Tests hängen daran), und ein viertes Zusammenlegen in einen einzigen
          Kasten würde die Frage „gehört das zusammen?" pro Leser neu aufwerfen. Eigener
          Wrapper `kauf__abrechnung` allein für den Abstand an diesem Einbauort. */}
      {!bearbeitung && (
        <div className="kauf__abrechnung">
          <Aufklapper faltung={abrechnungFaltung} titel="Abrechnung und Volumen"
            inhaltId="kauf-abrechnung-inhalt">
            {abrechnungFaltung.offen && (
              <>
                <Abrechnung client={client} customerId={kundeId} orderId={auftrag.id}
                  currency={auftrag.currency} />
                <AuftragsVolumen client={client} customerId={kundeId}
                  orderId={auftrag.id} currency={auftrag.currency} />
              </>
            )}
          </Aufklapper>
        </div>
      )}

      {/* Der Kasten „Anhänge" — am Auftrag NUR lesend, mit dem Ausblenden-Schalter je
          Anlage (Spec, Abschnitt Oberfläche): „kein Anhängen, kein Umsortieren, kein
          Löschen — die Anhänge kommen aus der Ableitung." Der Kasten wird das SELBST, weil
          sein Bezug `art: "order"` ist (siehe components/Anhaenge.tsx) — ein `nurLesend`
          von hier wäre redundant und, schlimmer, könnte den Ausblenden-Schalter mit
          abschalten, den es HIER anders als am eingefrorenen Angebot geben muss. KEIN
          Modal-Schlitz: Im Bearbeiten-Modal hätte der Kasten nichts zu bearbeiten (Auftrag
          kennt weder Anhängen noch Umsortieren), er steht deshalb NUR hier. Eigener
          Wrapper `kauf__anhaenge` allein für den Abstand an diesem Einbauort (kein
          geteilter Blockname). */}
      {!bearbeitung && (
        <div className="kauf__anhaenge">
          <Aufklapper faltung={anhaengeFaltung} titel="Anhänge"
            inhaltId="kauf-anhaenge-inhalt">
            {anhaengeFaltung.offen && (
              <Anhaenge client={client}
                bezug={{ art: "order", customerId: kundeId, vorgangId: auftrag.id }}
                idPraefix="kauf-lese"
                // Der Ausblenden-Schalter wirkt SOFORT auf den Server (Hausregel-Ausnahme,
                // siehe Kopfkommentar) und dreht dabei `auftrag.version` weiter
                // (`schreibweg` in vorgangsAttachment.ts bumpt dieselbe Spalte wie jeder
                // andere Schreibweg) — ohne dieses Fortschreiben liefe das nächste
                // Speichern der Positionen oder des Kopfes in eine 409 für eine Änderung,
                // die niemand fremd gemacht hat.
                version={auftrag.version}
                onVersionChange={(version) => {
                  p.onFortschreiben({ ...auftrag, version });
                }} />
            )}
          </Aufklapper>
        </div>
      )}

      {vorschauOffen && (
        <Belegvorschau
          titel={`Auftragsbestätigung ${auftrag.orderNumber} · Vorschau`}
          // Die FASSUNG: die Version, dieselbe Grösse wie am Angebot (Ruling K6).
          fassung={String(auftrag.version)}
          laden={() => client.vorschauOrder(kundeId, auftrag.id)}
          // Der PDF-Knopf der Vorschau geht den BESTEHENDEN Download-Weg — kein zweiter
          // Weg zum selben Ziel (Spec, wörtlich).
          aufPdf={() => { void alsPdf(); }}
          onSchliessen={() => setVorschauOffen(false)} />
      )}
    </>
  );
}
